import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '../sp-firebase/firebase';

/** On-demand Refinitiv jobs run by the VM worker (docs/market-data/refinitiv-on-demand.md). Superadmin only. */
const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';

export type JobType = 'probe' | 'validate' | 'backfill' | 'fetch_now';
export type JobStatus = 'queued' | 'running' | 'done' | 'failed' | 'cancelled';

export interface RefinitivJob<R = any> {
  id: number;
  type: JobType;
  params: any;
  status: JobStatus;
  progress: { chunk?: number; chunks?: number; line?: string } | null;
  result: R | null;
  error: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
}

export interface WorkerStatus {
  online: boolean;
  lastSeenAt: string | null;
  secondsAgo: number | null;
  worker: string | null;
  llm: boolean;
  queued: number;
  running: number;
}

export interface ChainRow { ric: string; name?: string | null; expiry?: string | null; currency?: string | null; last?: number | null; exchange?: string | null }
export interface Suggestion {
  root: string;
  ok: boolean;
  name: string | null;
  exchange: string | null;
  currency: string | null;
  ricMethod: 'monthly' | 'continuation' | 'spot_only' | null;
  ricYearStyle: '1_digit' | '2_digit' | null;
  tradingCalendar: number[] | null;
  futuresDepth: number | null;
  spotSuffix: string | null;
  contracts: Array<{ ric: string; maturityMonth: string; expiry: string | null; last: number | null }>;
  warnings: string[];
}
export interface ProbeResult {
  roots: Array<{ root: string; chainOk: boolean; chainError?: string | null; chain: ChainRow[] }>;
  suggestions: Suggestion[];
  llm: null | {
    commodity: string;
    exchangeName: string;
    quoteUnit: string;
    unitMultiplier: number;
    sourceUnit: string | null;
    summary: string;
    cautions: string[];
  };
}
export interface ValidateResult {
  today: string;
  ok: boolean;
  missing: string[];
  rics: Array<{ ric: string; role: 'spot' | 'live' | 'expired' | 'chain'; maturityMonth?: string; ok: boolean; price?: number; date?: string; rows?: number }>;
}
export interface FetchJobResult { pointsSaved: number; summary: string; log: string[] }

async function headers(): Promise<Record<string, string>> {
  const user: User | null = auth.currentUser ?? await new Promise((resolve) => {
    const off = onAuthStateChanged(auth, (u) => { off(); resolve(u); });
    setTimeout(() => { off(); resolve(null); }, 5000);
  });
  if (!user) throw new Error('Not authenticated');
  return { Authorization: `Bearer ${await user.getIdToken()}`, 'Content-Type': 'application/json' };
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API_URL}/api/v2/refinitiv${path}`, { method, headers: await headers(), ...(body ? { body: JSON.stringify(body) } : {}) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data as T;
}

export const getWorkerStatus = () => call<WorkerStatus>('GET', '/status');
export const createJob = async (type: JobType, params: Record<string, unknown>) =>
  (await call<{ job: RefinitivJob }>('POST', '/jobs', { type, params })).job;
export const getJob = async <R = any>(id: number) => (await call<{ job: RefinitivJob<R> }>('GET', `/jobs/${id}`)).job;
export const cancelJob = (id: number) => call<{ cancelled: boolean }>('POST', `/jobs/${id}/cancel`);
